import React, { useState } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { CampaignStatus } from '../../../../infrastructure/CampaignClient';
import type { CreateCampaignModalProps, FormData } from './types';

const CreateCampaignModal: React.FC<CreateCampaignModalProps> = ({ show, onHide, onSubmit, isSubmitting = false }) => {
  // Get current time in UTC for initial values
  const now = new Date();
  const utcThirtyDaysLater = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

  const [formData, setFormData] = useState<FormData>({
    name: '',
    startAt: now,
    endAt: utcThirtyDaysLater,
    status: CampaignStatus.DRAFT,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors['name'] = 'Campaign name is required';
    }

    if (formData.startAt >= formData.endAt) {
      newErrors['endAt'] = 'End date must be after start date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleReset = () => {
    const now = new Date();
    const utcThirtyDaysLater = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

    setFormData({
      name: '',
      startAt: now,
      endAt: utcThirtyDaysLater,
      status: CampaignStatus.DRAFT,
    });
    setErrors({});
  };

  const handleClose = () => {
    handleReset();
    onHide();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      onSubmit(formData);
      handleReset();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Create New Campaign</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Campaign Name*</Form.Label>
            <Form.Control
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className={errors['name'] ? 'is-invalid' : ''}
              placeholder="Enter campaign name"
            />
            {errors['name'] && <div className="invalid-feedback">{errors['name']}</div>}
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  Start Date* <small className="text-muted">(UTC)</small>
                </Form.Label>
                <DatePicker
                  selected={formData.startAt}
                  onChange={(date) => handleInputChange('startAt', date || new Date())}
                  className="form-control"
                  dateFormat="yyyy-MM-dd HH:mm"
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  timeCaption="Time"
                  minDate={new Date()}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  End Date* <small className="text-muted">(UTC)</small>
                </Form.Label>
                <DatePicker
                  selected={formData.endAt}
                  onChange={(date) => handleInputChange('endAt', date || formData.endAt)}
                  className={`form-control ${errors['endAt'] ? 'is-invalid' : ''}`}
                  dateFormat="yyyy-MM-dd HH:mm"
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  timeCaption="Time"
                  minDate={formData.startAt}
                />
                {errors['endAt'] && <div className="invalid-feedback d-block">{errors['endAt']}</div>}
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={formData.status}
              onChange={(e) => handleInputChange('status', e.target.value as CampaignStatus)}
            >
              <option value={CampaignStatus.DRAFT}>Draft</option>
              <option value={CampaignStatus.PUBLISHED}>Published</option>
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Creating...' : 'Create Campaign'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CreateCampaignModal;
